import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { skipToken } from '@reduxjs/toolkit/query';

import { Button, Heading, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { InfoRow, NotFound } from '@app/shared/ui/molecules/Common';
import { FindUserSection } from '@app/shared/ui/organisms/Admin/Common';
import { TransactionSection, UserInfoSection } from '@app/shared/ui/organisms/Admin/Balance';
import { Modal } from '@app/shared/ui/organisms/Common';
import { useSearchParamsUpdater } from '@app/shared/hooks/useSearchParamsUpdater';
import { useFindUser } from '@app/shared/hooks/admin/useFindUser';
import { useFindUserQuery, useUpdateUserBalanceMutation } from '@app/store/slices/admin/adminApi';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { formatBalance, formatNumber } from '@app/shared/utils/common';
import notification from '@app/shared/utils/notification';
import { TRANSACTION_PLACEHOLDERS_MAP } from '@app/shared/constants/admin/balance';
import { PriceType } from '@app/shared/types/common';
import { AdminBalanceAction } from '@app/store/slices/admin/adminTypes';
import { FindUserFilters } from '@app/shared/types/admin/common';

export default function AdminBalancePage() {
  const { t } = useTranslation();

  const { searchParams, updateSearchParams } = useSearchParamsUpdater<FindUserFilters>();
  const { findUserBy, handleFindUserByChange, findUserValue, handleFindUserValueChange } =
    useFindUser({ searchParams, updateSearchParams });

  const [transactionType, setTransactionType] = useState(AdminBalanceAction.TOP_UP);
  const [transactionAmount, setTransactionAmount] = useState(0);
  const [balanceType, setBalanceType] = useState(PriceType.COOKIE);
  const {
    value: isModalOpen,
    setValue: setIsModalOpen,
    setTrue: openModal,
    setFalse: closeModal,
  } = useBoolean(false);

  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    isError: isUserError,
  } = useFindUserQuery(
    findUserValue ? { value: findUserValue, searchType: findUserBy } : skipToken
  );
  const [updateUserBalance, { isLoading: isUpdatingUserBalance }] = useUpdateUserBalanceMutation();
  const { handleFetchError } = useApiErrorHandler();

  const handleUserBalanceUpdate = async () => {
    if (!user || isUpdatingUserBalance) {
      return;
    }

    try {
      await updateUserBalance({
        body: {
          userId: user.id,
          balanceType,
          action: transactionType,
          amount: transactionAmount,
        },
        findUserFilters: { value: findUserValue, searchType: findUserBy },
      }).unwrap();
      notification.success(t('admin_balance.update_success'));
      setTransactionAmount(0);
    } catch (err) {
      handleFetchError(err);
    } finally {
      closeModal();
    }
  };

  const cookieBalance = Number(user?.finance?.cookieBalance ?? 0);
  const tonBalance = Number(user?.finance?.tonBalance ?? 0);
  const isTransactionTypeCredit = transactionType === AdminBalanceAction.TOP_UP;
  const userBalance = balanceType === 'cookie' ? cookieBalance : tonBalance;
  const newUserBalance = isTransactionTypeCredit
    ? userBalance + transactionAmount
    : userBalance - transactionAmount;
  const formattedUserBalance = `${formatNumber(userBalance)} ${isTransactionTypeCredit ? '+' : '-'} ${formatNumber(transactionAmount)} = ${formatBalance(balanceType, newUserBalance)}`;

  const renderUserInfoSection = () => {
    if (isUserLoading || isUserFetching) {
      return <LoadingSpinner containerClassName="min-h-[280px]" />;
    } else if (user && !isUserError) {
      return (
        <UserInfoSection
          user={{
            id: user.id,
            telegramTag: user.telegramUsername,
            telegramId: user.telegramId,
            name: user.profile?.fullName,
            curator: user.curator?.telegramUsername,
            cookieBalance,
            tonBalance,
          }}
        />
      );
    }

    return <NotFound withLightBlueBg containerClassName="min-h-[280px]" />;
  };

  return (
    <div className="flex flex-col gap-2">
      <FindUserSection
        findUserBy={findUserBy}
        onFindUserByChange={handleFindUserByChange}
        findUserValue={findUserValue}
        onFindUserValueChange={handleFindUserValueChange}
      />

      {renderUserInfoSection()}

      <TransactionSection
        transactionType={transactionType}
        onTransactionTypeChange={setTransactionType}
        transactionAmount={transactionAmount}
        onTransactionAmountChange={setTransactionAmount}
        balanceType={balanceType}
        onBalanceTypeChange={setBalanceType}
        onTransactionBtnClick={openModal}
        isTransactionBtnDisabled={!transactionAmount || !user || isUserError}
      />

      <Modal isOpen={isModalOpen} onOpenChange={setIsModalOpen} hiddenTitle="Transaction">
        <Heading level={2}>{t('admin_balance.confirm')}</Heading>
        <div className="text-start">
          <InfoRow
            label={t('admin_common.telegram_tag')}
            labelClassName="text-foreground"
            value={user?.telegramUsername}
          />
          <InfoRow label="Telegram ID" labelClassName="text-foreground" value={user?.telegramId} />
          <InfoRow
            label={t('common.balance')}
            labelClassName="text-foreground"
            value={formattedUserBalance}
          />
        </div>
        <Button
          onClick={handleUserBalanceUpdate}
          disabled={transactionAmount <= 0 || isUpdatingUserBalance}
        >
          {t(`admin_balance.${TRANSACTION_PLACEHOLDERS_MAP[transactionType]}`)}
        </Button>
      </Modal>
    </div>
  );
}
